import { useParams } from 'react-router-dom'
import { LayoutDashboard, Target, Briefcase, Calendar, Info } from 'lucide-react'
import { useBusiness } from '@/hooks/useBusiness'
import { BusinessStage } from '@/lib/db/schema'
import { useState } from 'react'

export function BusinessDetailPage() {
  const { businessId } = useParams<{ businessId: string }>()
  const { business, isLoading, error, updateStage } = useBusiness(businessId)
  const [isUpdating, setIsUpdating] = useState(false)

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-500">
        <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p>Memuat overview bisnis...</p>
      </div>
    )
  }

  if (error || !business) {
    return (
      <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
        <p>Gagal memuat bisnis: {error || 'Bisnis tidak ditemukan'}</p>
      </div>
    )
  }

  const handleStageChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    setIsUpdating(true)
    await updateStage(e.target.value as BusinessStage)
    setIsUpdating(false)
  }

  return (
    <div className="space-y-6">
      {/* Header & Stage Updater */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 p-6 bg-slate-900 border border-slate-800 rounded-xl">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-bold text-slate-100">{business.name}</h1>
            <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded bg-slate-800 text-slate-400">
              {business.currency}
            </span>
          </div>
          <p className="text-sm text-slate-400 flex items-center gap-2">
            <Briefcase size={14} />
            {business.businessType || 'Belum ada kategori'}
          </p>
        </div>
        
        <div className="flex flex-col items-start sm:items-end gap-2">
          <label className="text-xs text-slate-500 font-medium">Status / Stage Bisnis</label>
          <div className="relative">
            <select 
              value={business.stage}
              onChange={handleStageChange}
              disabled={isUpdating}
              className="appearance-none bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-lg pl-3 pr-8 py-2 font-medium focus:outline-none focus:ring-2 focus:ring-brand-500 cursor-pointer disabled:opacity-50"
            >
              <option value="planning">Planning (Perencanaan)</option>
              <option value="running">Running (Berjalan)</option>
              <option value="scaling">Scaling (Ekspansi)</option>
              <option value="paused">Paused (Jeda sementara)</option>
              <option value="closed">Closed (Tutup)</option>
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
              ▼
            </div>
          </div>
        </div>
      </div>

      {/* Info Cards Placeholder */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-5 rounded-xl border border-slate-800 bg-slate-900/50">
          <div className="text-slate-500 mb-2"><Calendar size={18} /></div>
          <p className="text-xs text-slate-400 mb-1">Tanggal Mulai</p>
          <p className="text-sm font-semibold text-slate-200">{business.startDate || '-'}</p>
        </div>
        <div className="p-5 rounded-xl border border-slate-800 bg-slate-900/50">
          <div className="text-brand-500 mb-2"><LayoutDashboard size={18} /></div>
          <p className="text-xs text-slate-400 mb-1">Health Score</p>
          <p className="text-sm font-semibold text-slate-200">-</p>
          <p className="text-[10px] text-slate-500 mt-1">Belum ada data metrik</p>
        </div>
        <div className="p-5 rounded-xl border border-slate-800 bg-slate-900/50">
          <div className="text-emerald-500 mb-2"><Target size={18} /></div>
          <p className="text-xs text-slate-400 mb-1">Cash Balance</p>
          <p className="text-sm font-semibold text-slate-200">Rp 0</p>
        </div>
      </div>

      {/* Notes Section */}
      {business.notes && (
        <div className="p-5 rounded-xl border border-slate-800 bg-slate-900/30">
          <h3 className="text-sm font-medium text-slate-300 flex items-center gap-2 mb-2">
            <Info size={16} className="text-slate-500" />
            Catatan Bisnis
          </h3>
          <p className="text-sm text-slate-400 whitespace-pre-wrap">{business.notes}</p>
        </div>
      )}
    </div>
  )
}
